import { useState } from "react";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import { ThemeProvider } from "@mui/material/styles";
import { MuiTheme } from "../../styles/MuiDataTableStyle";
import { Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, TextField, Tooltip } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import PaymentIcon from "@mui/icons-material/Payment";
import TablePagination from "@material-ui/core/TablePagination";
import EditIcon from "@mui/icons-material/Edit";
import { PersonOffRounded, HowToRegRounded, CheckRounded, CloseRounded, InfoOutlined } from "@mui/icons-material";
import { textLabels, downloadOptions } from "../../utils/muiTableOptions";
import { SeePatientDetailsModal } from "../Modals/SeePatientDetailsModal";
import { PaymentDetailsModals } from "../Modals/PaymentDetailsModal";
import { InactivatePatientModal } from "../Modals/InactivatePatientModal";
import { ReactivatePatientModal } from "../Modals/ReactivatePatientModal";
import { PendingUnsubscriptionModal } from "../Modals/PendingUnsubscriptionModal";
import { useEffect } from "react";
import dayjs from "dayjs";
import { disablePatient, editPatient, activatePatient } from "../../services/patientsService";
import { useStateAuthValue } from "../../context/AuthState";
import { useToast } from "../../context/Toast/ToastProvider";
import { showToast, ToastType } from "../../utils/ToastUtil";
import { convertDateFormat } from "../../utils/dateForm";
import { editPayment } from "../../services/PaymentService";
import CreatePatient from "../../pages/createPatient";
import { Select } from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers";
import { Search } from "@mui/icons-material";

export const ClientTable = ({
  data,
  getPatientsData,
  setLoading,
  setLoadingPatients,
  loadingPatients,
  totalPages,
  setTotalPages,
  dataLength,
  currentPage,
}) => {
  const [{ userToken }] = useStateAuthValue();
  const toastDispatch = useToast();
  const [finalData, setFinalData] = useState(data);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);

  const [openCreateNewModal, setOpenCreateNewModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openInactivationModal, setOpenInactivationModal] = useState(false);
  const [openReactivationModal, setOpenReactivationModal] = useState(false);
  const [openPendingUnsubscriptionModal, setOpenPendingUnsubscriptionModal] = useState(false);
  const [user, setUser] = useState({});
  const [textSearch, setTextSearch] = useState(null);
  const [activated, setActivatedFilter] = useState(null);
  const [financerPlan, setFinancerPlan] = useState(null);
  const [financer, setFinancer] = useState(null);
  const [reason, setReason] = useState("");
  const [lastTextSearch, setLastTextSearch] = useState("");
  const [nameSearch, setNameSearch] = useState(null);
  const [lastNameSearch, setLastNameSearch] = useState("");
  const [exactMatch, setExactMatch] = useState(true);

  // Criterios vigentes: los dos inputs de búsqueda + filtros + modo exacto
  const currentCriteria = () => ({
    search: textSearch?.trim(),
    nameSearch: nameSearch?.trim(),
    activated,
    financerPlan,
    financer,
    exactMatch,
  });

  // Da vuelta el estado de una fila en la tabla sin refetchear.
  // Si la baja/alta dio OK, "activated" cambió sí o sí: no hace falta
  // preguntarle al back cómo quedó. La fila sigue matcheando la búsqueda por
  // email/DNI, solo cambia su badge.
  const setRowActivated = (id, isActive) => {
    setFinalData((prev) =>
      (prev ?? []).map((row) =>
        row?.id === id ? { ...row, activated: isActive } : row
      )
    );
  };

  // Tras un cambio de estado con OK: si hay filtro de Estado activo la fila
  // puede dejar de pertenecer a la lista -> refetch. Si no (caso normal:
  // búsqueda por email/DNI) basta con el update local instantáneo.
  const reconcileStateChange = (id, isActive) => {
    if (activated) {
      getPatientsData(currentCriteria());
    } else {
      setRowActivated(id, isActive);
    }
  };

  useEffect(() => {
    if (data == null && textLabels)
      options.textLabels.body.noMatch =
        "Realiza una busqueda para ver los registros.";
    else
      options.textLabels.body.noMatch =
        "Lo sentimos, no se encontraron registros";
    setFinalData(data);
  }, [data]);

  const displayError = (msg) =>
    showToast(toastDispatch, msg, ToastType.ERROR, ToastType.ERROR);

  const finishEditPatient = async (object) => {
    setOpenDetailsModal(false);
    setLoading(true);
    const newData = {
      ...user,
      ...object,
    };

    editPatient(userToken, newData, displayError).finally((e) => {
      showToast(
        toastDispatch,
        "Se creo con exito",
        ToastType.SUCCESS,
        ToastType.SUCCESS
      );

      setLoading(false);
    });

    setLoading(false);
    setUser({});
    setTimeout(() => {
      getPatientsData(currentCriteria());
    }, 1000);
  };

  const finishEditPayment = async (object) => {
    setOpenPaymentModal(false);
    setLoading(true);

    editPayment(userToken, user.id, object, displayError).finally(() => {
      setLoading(false);
    });

    setUser({});
    setTimeout(() => {
      getPatientsData(currentCriteria());
    }, 1000);
  };

  const activateDisabledPatient = async (object) => {
    setOpenReactivationModal(false);
    const targetId = object?.id;
    setLoading(true);

    // El servicio resuelve con response.data en OK y con undefined si falló
    // (ahí ya mostró el toast de error). Atamos el update al resultado real,
    // no a un setTimeout a ciegas.
    const result = await activatePatient(userToken, object, displayError);
    setLoading(false);

    if (result === undefined) return;
    reconcileStateChange(targetId, true);
  };

  const inactivatePatient = async (values) => {
    setOpenInactivationModal(false);
    const targetId = user?.id;
    const product = user?.product;
    setLoading(true);

    const result = await disablePatient(
      userToken,
      targetId,
      reason,
      product,
      values?.notify ?? true,
      displayError
    );
    setLoading(false);
    setUser({});

    if (result === undefined) return;

    if (result.message === "success") {
      showToast(
        toastDispatch,
        `Baja registrada. Se hará efectiva el ${dayjs(result.deactivationDate).format("DD/MM/YYYY")}.`,
        ToastType.SUCCESS,
        ToastType.SUCCESS
      );
      // El DELETE no devuelve el unsubscriptionId nuevo: hace falta refetchear
      // para que la fila pase a reflejar el tercer estado ("Baja en proceso").
      getPatientsData(currentCriteria());
      return;
    }

    if (result.message === "error.pendingUnsubscriptionExists") {
      showToast(
        toastDispatch,
        `Ya hay una baja en curso para este socio, se hace efectiva el ${dayjs(result.effectiveDate).format("DD/MM/YYYY")}.`,
        ToastType.INFO,
        ToastType.INFO
      );
    }
  };

  const columns = [
    {
      name: "created_data",
      label: "Fecha de alta",
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          return <p>{convertDateFormat(finalData?.[dataIndex]?.created_data)}</p>;
        },
      },
    },
    {
      name: "lastName",
      label: "Apellido",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "firstName",
      label: "Nombres",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "id_number",
      label: "DNI",
      options: {
        filter: false,
        sort: true,
      },
    },

    {
      name: "phone",
      label: "Celular",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "email",
      label: "Correo",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "financer_plan",
      label: "Plan",
      options: {
        filter: true,
        sort: true,
        filterType: "custom",
        // Controlado desde el estado para que los chips de "filtros activos"
        // y el popover de filtros queden siempre en sync
        filterList: financerPlan ? [financerPlan] : [],
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            const optionValues = [
              "ASISTENCIA MEDICA",
              "CLASSIC",
              "CLASSIC BNF",
              "ClassicGR",
              "PLUS",
              "PLUS BNF",
              "PlusGR",
              "PREMIUM",
              "PREMIUM BNF",
              "VEB07",
              "VEB14",
              "VEB30"
            ];
            return (
              <FormControl variant="standard" style={{ width: "12rem" }}>
                <InputLabel id="filter-plan-label">Plan</InputLabel>
                <Select
                  labelId="filter-plan-label"
                  value={filterList[index][0] ?? ""}
                  onChange={(event) => {
                    onChange(
                      event.target.value === "" ? [] : [event.target.value],
                      index,
                      column
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {optionValues.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          },
        },
      },
    },
    // {
    //   name: "financer",
    //   label: "Financiador",
    //   options: {
    //     filter: true,
    //     sort: true,
    //     filterType: "custom",
    //     filterOptions: {
    //       display: (filterList, onChange, index, column) => {
    //         const optionValues = financersNameList;
    //         return (
    //           <FormControl style={{ width: "10rem" }}>
    //             <InputLabel htmlFor="select-multiple-chip">Empresa</InputLabel>
    //             <Select
    //               value={filterList[index]}
    //               onChange={(event) => {
    //                 onChange(
    //                   event.target.value === "" ? [] : [event.target.value],
    //                   index,
    //                   column
    //                 );
    //               }}
    //             >
    //               {optionValues.map((item) => (
    //                 <MenuItem key={item} value={item}>
    //                   <ListItemText primary={item} />
    //                 </MenuItem>
    //               ))}
    //             </Select>
    //           </FormControl>
    //         );
    //       },
    //     },
    //   },
    // },
    // {
    //   name: "agreement_id",
    //   label: "Nro. Convenio",
    //   options: {
    //     filter: false,
    //     sort: true,
    //   },
    // },
    // {
    //   name: "product",
    //   label: "Producto",
    //   options: {
    //     filter: false,
    //     sort: true,
    //   },
    // },
    {
      name: "estado",
      label: "Estado",
      options: {
        filter: true,
        sort: false,
        filterType: "custom",
        filterList: activated ? [activated] : [],
        filterOptions: {
          display: (filterList, onChange, index, column) => {
            return (
              <FormControl variant="standard" style={{ width: "12rem" }}>
                <InputLabel id="filter-estado-label">Estado</InputLabel>
                <Select
                  labelId="filter-estado-label"
                  value={filterList[index][0] ?? ""}
                  onChange={(event) => {
                    onChange(
                      event.target.value === "" ? [] : [event.target.value],
                      index,
                      column
                    );
                  }}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  <MenuItem value="ACTIVO">Activo</MenuItem>
                  <MenuItem value="INACTIVO">Inactivo</MenuItem>
                </Select>
              </FormControl>
            );
          },
        },
        // Badge no interactivo: solo comunica el estado del socio.
        // La acción de baja/alta vive en la columna "Acciones" (design.md §7).
        customBodyRenderLite: (dataIndex) => {
          // finalData (no la prop data): es lo que la tabla realmente renderiza;
          // data puede quedar desfasada/undefined hasta que corre el useEffect
          const active = finalData?.[dataIndex]?.activated;
          const pendingUnsubscriptionId = finalData?.[dataIndex]?.unsubscriptionId;

          // 3 estados: activated=false -> Inactivo; activated=true sin
          // unsubscriptionId -> Activo; activated=true con
          // unsubscriptionId -> Baja en proceso (cooling-off en curso).
          const status = !active
            ? {
                label: "Inactivo",
                color: "#5a6470",
                background: "rgba(90,100,112,0.1)",
                dot: "#9aa1aa",
              }
            : pendingUnsubscriptionId
            ? {
                label: "Baja en proceso",
                color: "#b84d1f",
                background: "rgba(235,104,54,0.12)",
                dot: "#EB6836",
              }
            : {
                label: "Activo",
                color: "#1b7a43",
                background: "rgba(39,174,96,0.12)",
                dot: "#27ae60",
              };

          return (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 700,
                color: status.color,
                background: status.background,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: status.dot,
                }}
              />
              {status.label}
            </Box>
          );
        },
      },
    },
    {
      name: "acciones",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          return (
            <div className="icons-container">
              <EditIcon
                id={finalData?.[dataIndex]?.usrId}
                className="icon edit-icon"
                onClick={() => {
                  setUser(finalData[dataIndex]);
                  setOpenDetailsModal(true);
                }}
              />
              <PaymentIcon
                id={finalData?.[dataIndex]?.usrId}
                className="icon"
                onClick={() => {
                  setUser(finalData[dataIndex]);
                  setOpenPaymentModal(true);
                }}
              />
              {finalData?.[dataIndex]?.activated ? (
                !finalData?.[dataIndex]?.unsubscriptionId && (
                  <Tooltip title="Dar de baja">
                    <PersonOffRounded
                      id={finalData[dataIndex]?.usrId}
                      className="icon"
                      sx={{ color: "#DA2037" }}
                      onClick={() => {
                        setUser(finalData[dataIndex]);
                        setOpenInactivationModal(true);
                      }}
                    />
                  </Tooltip>
                )
              ) : (
                <Tooltip title="Reactivar socio">
                  <HowToRegRounded
                    id={finalData[dataIndex]?.usrId}
                    className="icon"
                    sx={{
                      color: "#002C54",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setUser(finalData[dataIndex]);
                      setOpenReactivationModal(true);
                    }}
                  />
                </Tooltip>
              )}
              {finalData?.[dataIndex]?.unsubscriptionId && (
                <Tooltip title="Ver baja en curso">
                  <InfoOutlined
                    id={finalData[dataIndex]?.usrId}
                    className="icon"
                    sx={{ color: "#EB6836", cursor: "pointer" }}
                    onClick={() => {
                      setUser(finalData[dataIndex]);
                      setOpenPendingUnsubscriptionModal(true);
                    }}
                  />
                </Tooltip>
              )}
              {/*
              <Diversity3Icon
                id={finalData[dataIndex].usrId}
                className="icon people-icon"
                onClick={() => {}}
              />
              <FeedIcon
                id={finalData[dataIndex].usrId}
                className="icon paper-icon"
                onClick={() => {}}
              />
              */}
            </div>
          );
        },
      },
    },
  ];
  // Vacía la tabla y vuelve al mensaje inicial (mismo comportamiento que la X del buscador)
  const clearResults = () => {
    options.textLabels.body.noMatch =
      "Realiza una busqueda para ver los registros.";
    setFinalData([]);
  };

  // Re-consulta con los criterios vigentes menos el que se sacó; si no queda
  // ningún criterio, limpia la tabla en vez de traer todo el padrón.
  const refetchWith = (overrides) => {
    const next = {
      search: lastTextSearch,
      nameSearch: lastNameSearch,
      activated,
      financerPlan,
      financer,
      exactMatch,
      ...overrides,
    };
    if (
      next.search?.trim() ||
      next.nameSearch?.trim() ||
      next.activated ||
      next.financerPlan ||
      next.financer
    ) {
      getPatientsData(next);
    } else {
      clearResults();
    }
  };

  // Ejecuta la búsqueda con lo tipeado en ambos inputs + filtros
  const runSearch = () => {
    setLastTextSearch(textSearch?.trim());
    setLastNameSearch(nameSearch?.trim());
    getPatientsData(currentCriteria());
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      runSearch();
    }
  };
  const customSearchRender = (
    searchText,
    handleSearch,
    hideSearch,
    _options
  ) => {
    // Chips de filtros activos: muestran cada criterio vigente y permiten sacarlo
    const activeChips = [];
    if (lastTextSearch?.trim()) {
      activeChips.push({
        key: "search",
        label: `Email/DNI: ${lastTextSearch}`,
        onDelete: () => {
          setTextSearch("");
          setLastTextSearch("");
          refetchWith({ search: null });
        },
      });
    }
    if (lastNameSearch?.trim()) {
      activeChips.push({
        key: "nameSearch",
        label: `Nombre: ${lastNameSearch}`,
        onDelete: () => {
          setNameSearch("");
          setLastNameSearch("");
          refetchWith({ nameSearch: null });
        },
      });
    }
    if (financerPlan) {
      activeChips.push({
        key: "plan",
        label: `Plan: ${financerPlan}`,
        onDelete: () => {
          setFinancerPlan(null);
          refetchWith({ financerPlan: null });
        },
      });
    }
    if (activated) {
      activeChips.push({
        key: "estado",
        label: `Estado: ${activated === "ACTIVO" ? "Activo" : "Inactivo"}`,
        onDelete: () => {
          setActivatedFilter(null);
          refetchWith({ activated: null });
        },
      });
    }

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <IconButton onClick={runSearch}>
            <Search></Search>
          </IconButton>
          <TextField
            variant="standard"
            value={textSearch ?? ""}
            onChange={(event) => setTextSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Email o DNI"
            sx={{ width: "180px", marginRight: "16px" }}
            InputProps={{
              "aria-label": "Buscar por email o DNI",
            }}
          />
          <TextField
            variant="standard"
            value={nameSearch ?? ""}
            onChange={(event) => setNameSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Nombre o apellido"
            sx={{ width: "180px" }}
            InputProps={{
              "aria-label": "Buscar por nombre o apellido",
            }}
          />
          <Tooltip title="Limpiar búsqueda">
            <IconButton
              onClick={() => {
                setTextSearch("");
                setNameSearch("");
                setLastTextSearch("");
                setLastNameSearch("");
                clearResults();
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="El valor debe coincidir completo (DNI entero sin puntos, email completo). Es más rápida.">
            <Chip
              // ✓ activa / ✕ apagada: el icono comunica el estado del toggle
              icon={
                exactMatch ? (
                  <CheckRounded sx={{ fontSize: 16 }} />
                ) : (
                  <CloseRounded sx={{ fontSize: 16 }} />
                )
              }
              label={exactMatch ? "Búsqueda exacta: sí" : "Búsqueda exacta: no"}
              clickable
              variant={exactMatch ? "filled" : "outlined"}
              onClick={() => {
                const next = !exactMatch;
                setExactMatch(next);
                // Si hay una búsqueda activa, re-ejecutarla con el nuevo modo
                if (lastTextSearch?.trim() || lastNameSearch?.trim()) {
                  getPatientsData({ ...currentCriteria(), exactMatch: next });
                }
              }}
              sx={{
                marginLeft: "8px",
                fontWeight: 600,
                borderRadius: "20px",
                transition: "0.2s ease-in-out all",
                ...(exactMatch
                  ? {
                      background: "linear-gradient(195deg, #002C54, #003f77)",
                      color: "white",
                      "& .MuiChip-icon": { color: "white" },
                      "&:hover": {
                        background: "linear-gradient(195deg, #003F77, #003f77)",
                      },
                    }
                  : {
                      color: "#5a6470",
                      borderColor: "#d4d8dd",
                      "& .MuiChip-icon": { color: "#9aa1aa" },
                      "&:hover": { background: "#f5f6f8" },
                    }),
              }}
            />
          </Tooltip>
          {loadingPatients && (
            <CircularProgress
              size={24}
              color="primary"
              sx={{ marginLeft: "12px" }}
            />
          )}
        </div>
        {activeChips.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "0.5rem",
              padding: "2px 0 8px 6px",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                color: "#757575",
              }}
            >
              Filtros activos
            </Box>
            {activeChips.map((chip) => (
              <Chip
                key={chip.key}
                label={chip.label}
                onDelete={chip.onDelete}
                sx={{
                  background: "rgba(0,44,84,0.1)",
                  color: "#002C54",
                  fontWeight: 600,
                  borderRadius: "20px",
                  transition: "0.2s ease-in-out all",
                  "& .MuiChip-deleteIcon": {
                    color: "rgba(0,44,84,0.45)",
                    "&:hover": { color: "#002C54" },
                  },
                }}
              />
            ))}
            {activeChips.length > 1 && (
              <Button
                size="small"
                onClick={() => {
                  handleSearch("");
                  setLastTextSearch("");
                  setFinancerPlan(null);
                  setActivatedFilter(null);
                  clearResults();
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#5a6470",
                  "&:hover": { background: "#f5f6f8" },
                }}
              >
                Limpiar todo
              </Button>
            )}
          </Box>
        )}
      </div>
    );
  };
  const options = {
    // Panel de búsqueda fijo: no se cierra al abrir el popover de filtros
    searchAlwaysOpen: true,
    selectableRows: "none",
    textLabels,
    downloadOptions,
    filterType: "dropdown",
    responsive: "standard",
    draggableColumns: { enabled: true },
    enableNestedDataAccess: ".",
    page: currentPage,
    count: totalPages,
    serverSide: true,
    pagination: false,
    elevation: 0,
    customSearchRender,
    onTableChange: (action, tableState) => {
      if (action === "filterChange" || action === "resetFilters") {
        // Resolver cada filtro por nombre de columna (los índices cambian
        // si se agregan/quitan columnas)
        const filterFor = (name) => {
          const idx = tableState?.columns?.findIndex((c) => c.name === name);
          return idx >= 0 ? tableState.filterList?.[idx]?.[0] ?? null : null;
        };
        const _plan = filterFor("financer_plan");
        const _activated = filterFor("estado");
        setFinancerPlan(_plan);
        setActivatedFilter(_activated);

        getPatientsData({
          ...currentCriteria(),
          activated: _activated,
          financerPlan: _plan,
        });
      }
    },
    customToolbar: () => {
      return (
        <Tooltip
          title="Crear Usuario"
          onClick={() => {
            setOpenCreateNewModal(true);
          }}
        >
          <IconButton>
            <AddCircleIcon></AddCircleIcon>
          </IconButton>
        </Tooltip>
      );
    },
  };

  //GET FINANCERS
  const [financersNameList, setFinancersNameList] = useState([]);
  const getFinancersNameList = async (token) => {
    return await axios
      .get(
        `${process.env.REACT_APP_BASE_URL}/services/parametrics/api/financers/get-name-list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setFinancersNameList(response.data);
      })
      .catch((error) => {
        console.log(error.response.data);
      });
  };
  useEffect(() => {
    getFinancersNameList(userToken);
  }, []);

  return (
    <ThemeProvider theme={MuiTheme}>
      <Box
        sx={{
          width: "100%",
          "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-head": {
            span: {
              justifyContent: "center",
            },
          },
        }}
      >
        <MUIDataTable
          title=""
          data={finalData != null ? finalData : []}
          columns={columns}
          options={options}
          // Los filtros activos se muestran con chips propios en la barra de
          // búsqueda; se suprime la fila de chips default de mui-datatables
          components={{ TableFilterList: () => null }}
        />

        <TablePagination
          component="div"
          count={totalPages}
          page={currentPage}
          onPageChange={(state, number) => {
            getPatientsData({
              ...currentCriteria(),
              page: number,
            });
          }}
          labelRowsPerPage=""
          rowsPerPage={dataLength}
          rowsPerPageOptions={[dataLength]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />

        <CreatePatient
          open={openCreateNewModal}
          setOpen={setOpenCreateNewModal}
          getPatientsData={() => {
            setLoading(true);
            setTimeout(() => {
              getPatientsData();
              setLoading(false);
            }, 2000);
          }}
          setLoading={(e) => {
            setLoading(e);
          }}
        />
        {openDetailsModal && (
          <SeePatientDetailsModal
            open={openDetailsModal}
            setOpen={(param) => {
              setOpenDetailsModal(param);
              if (!param) {
                setUser({});
              }
            }}
            handleClose={finishEditPatient}
            patient={user}
          />
        )}
        {openPaymentModal && (
          <PaymentDetailsModals
            open={openPaymentModal}
            setOpen={(param) => {
              setOpenPaymentModal(param);
              if (!param) {
                setUser({});
              }
            }}
            handleClose={finishEditPayment}
            patient={user}
          />
        )}

        <InactivatePatientModal
          open={openInactivationModal}
          setOpen={setOpenInactivationModal}
          setReason={setReason}
          reason={reason}
          handleClose={inactivatePatient}
          patient={user}
        />

        <ReactivatePatientModal
          open={openReactivationModal}
          setOpen={setOpenReactivationModal}
          handleConfirm={activateDisabledPatient}
          patient={user}
        />

        <PendingUnsubscriptionModal
          open={openPendingUnsubscriptionModal}
          setOpen={setOpenPendingUnsubscriptionModal}
          patient={user}
        />
      </Box>
    </ThemeProvider>
  );
};
